import { AlertTriangle, FlaskConical, Loader2, Play, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import type { ExtractionRun } from '@/services/api/cases'

export interface ExtractionStatusCardProps {
  /** The extraction in progress (one run, or the sum of several); undefined when none is going. */
  runInProgress: ExtractionRun | undefined
  pendingCount: number
  failedCount: number
  autoRunning: boolean
  onViewRunDetails: () => void
  onRunPending: () => void
  onRetryFailed: () => void
}

const BANNER = 'mx-4 mt-3 rounded-lg border px-4 py-3 md:mx-6'

/**
 * Pure status banner shown above the document list. Renders one of three
 * mutually-exclusive states (running run / pending docs / failed docs) or
 * nothing. All IO is delegated to callbacks.
 */
export function ExtractionStatusCard({
  runInProgress,
  pendingCount,
  failedCount,
  autoRunning,
  onViewRunDetails,
  onRunPending,
  onRetryFailed,
}: ExtractionStatusCardProps) {
  const { t } = useTranslation()
  if (runInProgress) {
    const done = runInProgress.completed_documents + runInProgress.failed_documents
    const pct =
      runInProgress.progress_percent ??
      (runInProgress.total_documents > 0
        ? Math.round((done / runInProgress.total_documents) * 100)
        : 0)
    const k = 'cases.documents.extractionCard'
    const parts: string[] = []
    if (runInProgress.running_documents > 0)
      parts.push(t(`${k}.statusRunning`, { count: runInProgress.running_documents }))
    if (runInProgress.pending_documents > 0)
      parts.push(t(`${k}.statusPending`, { count: runInProgress.pending_documents }))
    if (runInProgress.completed_documents > 0)
      parts.push(t(`${k}.statusDone`, { count: runInProgress.completed_documents }))
    if (runInProgress.failed_documents > 0)
      parts.push(t(`${k}.statusFailed`, { count: runInProgress.failed_documents }))
    const label =
      parts.length > 0
        ? `${t(`${k}.extracting`)} · ${parts.join(' · ')}`
        : t(`${k}.extractingCount`, { count: runInProgress.total_documents })
    return (
      <div className={`${BANNER} border-border bg-muted/40`}>
        <div className="flex items-center gap-3">
          <Loader2 className="size-4 shrink-0 animate-spin text-primary" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-foreground">{label}</div>
            <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={onViewRunDetails} className="shrink-0">
            {t('cases.documents.extractionCard.viewDetails')}
          </Button>
        </div>
      </div>
    )
  }

  if (pendingCount > 0) {
    return (
      <div className={`${BANNER} border-border bg-muted/40`}>
        <div className="flex items-center gap-3">
          <FlaskConical className="size-4 shrink-0 text-primary" />
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-foreground">
              {t('cases.documents.extractionCard.waiting', { count: pendingCount })}
            </div>
            <div className="text-xs text-muted-foreground">
              {t('cases.documents.extractionCard.pipelineHint')}
            </div>
          </div>
          <Button size="sm" onClick={onRunPending} disabled={autoRunning} className="shrink-0">
            {autoRunning ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <>
                <Play className="size-3.5 mr-1.5" />
                {t('cases.documents.extractionCard.run')}
              </>
            )}
          </Button>
        </div>
      </div>
    )
  }

  if (failedCount > 0) {
    return (
      <div className={`${BANNER} border-warning-border bg-warning-surface`}>
        <div className="flex items-center gap-3">
          <AlertTriangle className="size-4 shrink-0 text-warning-foreground" />
          <div className="flex-1 min-w-0 text-sm font-medium text-foreground">
            {t('cases.documents.extractionCard.failed', { count: failedCount })}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onRetryFailed}
            disabled={autoRunning}
            className="shrink-0"
          >
            {autoRunning ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <>
                <RotateCcw className="size-3.5 mr-1.5" />
                {t('cases.documents.extractionCard.retry')}
              </>
            )}
          </Button>
        </div>
      </div>
    )
  }

  return null
}
